import { splitPeriodWeekRanges } from "./periods";
import { installmentAvailableFrom, installmentIsDue } from "./installments";
import type { CardConfig, PendingItem } from "./types";

const DAY_MILLISECONDS = 86_400_000;

export type FinancialAgendaEventKind =
  | "week_change"
  | "installment"
  | "promised_funding"
  | "period_end"
  | "card_cut"
  | "card_payment";

export interface FinancialAgendaEvent {
  id: string;
  kind: FinancialAgendaEventKind;
  date: string;
  title: string;
  description: string;
  amountCents?: number;
  commitmentCents?: number;
  card?: string;
  pendingItemId?: string;
  /** La agenda sólo anticipa fechas; una acción nace cuando el pendiente vence. */
  actionable: false;
}

export interface BuildFinancialAgendaInput {
  today: string;
  horizonDays?: number;
  pendingItems: readonly PendingItem[];
  cards: readonly CardConfig[];
  periodLabel?: string;
  paymentTargets?: Array<{ card: string; period: string; targetOn: string | null }>;
  periodStart?: string;
  periodEnd?: string;
}

function isoTimestamp(value: string | undefined | null): number | null {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return null;
  return timestamp;
}

function isoDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function addDays(value: string, days: number): string | null {
  const timestamp = isoTimestamp(value);
  return timestamp == null ? null : isoDate(timestamp + days * DAY_MILLISECONDS);
}

function dateAtClampedDay(year: number, monthIndex: number, day: number): string {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return isoDate(Date.UTC(year, monthIndex, Math.min(Math.max(1, Math.round(day)), lastDay)));
}

function monthCandidate(today: string, monthOffset: number, day: number): string | null {
  const timestamp = isoTimestamp(today);
  if (timestamp == null) return null;
  const parsed = new Date(timestamp);
  return dateAtClampedDay(parsed.getUTCFullYear(), parsed.getUTCMonth() + monthOffset, day);
}

export function nextCardCutDate(today: string, cutDay: number): string | null {
  const current = isoTimestamp(today);
  if (current == null || !Number.isFinite(cutDay)) return null;
  for (const monthOffset of [0, 1, 2]) {
    const candidate = monthCandidate(today, monthOffset, cutDay);
    if (candidate && isoTimestamp(candidate)! > current) return candidate;
  }
  return null;
}

export function nextCardPaymentDate(today: string, cutDay: number, paymentDays: number): string | null {
  const current = isoTimestamp(today);
  if (current == null || !Number.isFinite(cutDay) || !Number.isFinite(paymentDays) || paymentDays < 0) return null;
  const candidates = [-1, 0, 1, 2]
    .map((monthOffset) => monthCandidate(today, monthOffset, cutDay))
    .map((cut) => cut ? addDays(cut, Math.round(paymentDays)) : null)
    .filter((candidate): candidate is string => Boolean(candidate) && isoTimestamp(candidate)! > current)
    .sort();
  return candidates[0] ?? null;
}

export function isPendingItemActionableNow(item: PendingItem, today: string): boolean {
  const todayTimestamp = isoTimestamp(today);
  if (todayTimestamp == null) return true;
  return item.pendingCents > 0 && installmentIsDue({ ...item, cardPeriod: item.period }, today);
}

function isWithinFutureWindow(date: string | undefined, start: number, end: number): date is string {
  const timestamp = isoTimestamp(date);
  return timestamp != null && timestamp > start && timestamp <= end;
}

export function buildFinancialAgenda(input: BuildFinancialAgendaInput): FinancialAgendaEvent[] {
  const today = isoTimestamp(input.today);
  if (today == null) return [];
  const horizonDays = Math.min(365, Math.max(1, Math.round(input.horizonDays ?? 30)));
  const horizonEnd = today + horizonDays * DAY_MILLISECONDS;
  const events: FinancialAgendaEvent[] = [];

  const weekRanges = splitPeriodWeekRanges(input.periodStart, input.periodEnd);
  if (weekRanges && isWithinFutureWindow(weekRanges.week2.start, today, horizonEnd)) {
    events.push({
      id: `week-change:${weekRanges.week2.start}`,
      kind: "week_change",
      date: weekRanges.week2.start,
      title: "Comienza Semana 2",
      description: "Banculem cambiará la semana activa por fecha; no moverá dinero por sí solo.",
      actionable: false,
    });
  }

  for (const item of input.pendingItems) {
    const availableFrom = installmentAvailableFrom({ ...item, cardPeriod: item.period });
    if (availableFrom && item.pendingCents > 0 && isWithinFutureWindow(availableFrom, today, horizonEnd)) {
      events.push({
        id: `installment:${item.id}:${availableFrom}`,
        kind: "installment",
        date: availableFrom,
        title: item.description,
        description: `${item.card} · mensualidad ${item.installmentNumber ?? ""} de ${item.installmentCount ?? ""} · corte ${item.period}. Desde esta fecha podrás apartarla completa.`,
        amountCents: item.pendingCents,
        commitmentCents: item.ownPendingCents,
        card: item.card,
        pendingItemId: item.id,
        actionable: false,
      });
    }
    if (item.ownPendingCents > 0 && isWithinFutureWindow(item.promiseDate, today, horizonEnd)) {
      events.push({
        id: `promised-funding:${item.id}:${item.promiseDate}`,
        kind: "promised_funding",
        date: item.promiseDate,
        title: `Completar apartado · ${item.description}`,
        description: `${item.card} · fecha que registraste para cubrir el resto de tu parte. Puedes abonarlo antes desde Ahora.`,
        amountCents: item.ownPendingCents,
        commitmentCents: item.ownPendingCents,
        card: item.card,
        pendingItemId: item.id,
        actionable: false,
      });
    }
  }

  if (isWithinFutureWindow(input.periodEnd, today, horizonEnd)) {
    events.push({
      id: `period-end:${input.periodEnd}`,
      kind: "period_end",
      date: input.periodEnd,
      title: `Cierra ${input.periodLabel ?? "la quincena"}`,
      description: "Es el último día del periodo. El cierre seguirá requiriendo su flujo normal y conservará los importes históricos.",
      actionable: false,
    });
  }

  for (const card of input.cards.filter((candidate) => candidate.active)) {
    const cutDate = nextCardCutDate(input.today, card.cutDay);
    if (cutDate && isWithinFutureWindow(cutDate, today, horizonEnd)) {
      events.push({
        id: `card-cut:${card.id}:${cutDate}`,
        kind: "card_cut",
        date: cutDate,
        title: `Corte · ${card.name}`,
        description: "Fecha informativa calculada con la configuración de la tarjeta; no cierra ni paga movimientos automáticamente.",
        card: card.paymentMethod,
        actionable: false,
      });
    }
    if (input.paymentTargets || card.paymentDays == null) continue;
    const paymentDate = nextCardPaymentDate(input.today, card.cutDay, card.paymentDays);
    if (paymentDate && isWithinFutureWindow(paymentDate, today, horizonEnd)) {
      events.push({
        id: `card-payment:${card.id}:${paymentDate}`,
        kind: "card_payment",
        date: paymentDate,
        title: `Pago estimado · ${card.name}`,
        description: "Fecha de referencia según los días configurados después del corte. El pago sólo ocurre cuando tú lo confirmas.",
        card: card.paymentMethod,
        actionable: false,
      });
    }
  }

  for (const target of input.paymentTargets ?? []) {
    if (target.targetOn && isoTimestamp(target.targetOn)! >= today && isoTimestamp(target.targetOn)! <= horizonEnd) events.push({
      id: `card-payment:${target.card}:${target.period}:${target.targetOn}`, kind: "card_payment", date: target.targetOn,
      title: `Pago objetivo · ${target.card}`, description: "Fecha objetivo precautoria de Banculem para un corte impago. No es la fecha límite bancaria ni registra un pago.",
      card: target.card, actionable: false,
    });
  }

  const priority: Record<FinancialAgendaEventKind, number> = {
    week_change: 0,
    installment: 1,
    promised_funding: 2,
    period_end: 3,
    card_cut: 4,
    card_payment: 5,
  };
  return events.sort((left, right) => left.date.localeCompare(right.date) || priority[left.kind] - priority[right.kind] || left.id.localeCompare(right.id));
}

export function futureCommitmentCents(events: readonly FinancialAgendaEvent[]): number {
  return events.reduce((sum, event) => sum + Math.max(0, event.commitmentCents ?? 0), 0);
}
